import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getComicById, getChaptersByComicId } from '@/mocks/comics';

const READER_PLACEHOLDER_IMAGES = [
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20in%20Japanese%20style%2C%20vertical%20scroll%20format%2C%20clean%20lineart%2C%20action%20scene%20with%20characters%2C%20detailed%20background%20and%20dynamic%20composition%2C%20professional%20comic%20illustration&width=800&height=600&seq=reader-page-01&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20dramatic%20dialogue%20scene%2C%20close%20up%20character%20expressions%2C%20speed%20lines%2C%20professional%20Japanese%20manga%20style%2C%20vertical%20layout&width=800&height=600&seq=reader-page-02&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20page%20showing%20an%20epic%20battle%20scene%2C%20two%20characters%20clashing%2C%20explosive%20effects%2C%20speed%20lines%2C%20professional%20Japanese%20manga%20art%20style%2C%20vertical&width=800&height=600&seq=reader-page-03&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20scenic%20landscape%20panel%2C%20beautiful%20background%20art%2C%20characters%20in%20nature%2C%20professional%20Japanese%20manga%20illustration%20style%2C%20vertical%20format&width=800&height=600&seq=reader-page-04&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20page%20with%20emotional%20character%20moment%2C%20soft%20shading%2C%20intimate%20scene%2C%20professional%20Japanese%20comic%20art%20style%2C%20vertical%20layout%2C%20detailed%20expressions&width=800&height=600&seq=reader-page-05&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20transformation%20sequence%2C%20energy%20burst%20effects%2C%20dramatic%20panel%20layout%2C%20professional%20Japanese%20manga%20style%2C%20vertical%20format&width=800&height=600&seq=reader-page-06&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20page%20with%20group%20of%20characters%20assembled%2C%20team%20pose%2C%20epic%20composition%2C%20professional%20Japanese%20comic%20art%2C%20vertical%20layout&width=800&height=600&seq=reader-page-07&orientation=portrait',
  'https://readdy.ai/api/search-image?query=A%20black%20and%20white%20manga%20comic%20page%20with%20climactic%20finale%20moment%2C%20dramatic%20lighting%2C%20full%20page%20spread%2C%20professional%20Japanese%20manga%20style%2C%20vertical%20format&width=800&height=600&seq=reader-page-08&orientation=portrait',
];

export default function ComicPage() {
  const { id } = useParams<{ id: string }>();
  const comicId = parseInt(id || '1');
  const comic = getComicById(comicId);
  const chapters = getChaptersByComicId(comicId);
  const [readingMode, setReadingMode] = useState(false);
  const [currentChapter, setCurrentChapter] = useState(chapters[0]?.id ?? null);
  const [chapterFilter, setChapterFilter] = useState<'all' | 'new'>('all');

  const displayedChapters = useMemo(() => {
    if (chapterFilter === 'new') return chapters.slice(0, 20);
    return chapters;
  }, [chapters, chapterFilter]);

  if (!comic) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
        <i className="ri-error-warning-line text-5xl text-foreground-300 w-12 h-12 flex items-center justify-center mx-auto"></i>
        <p className="mt-4 text-foreground-600">漫画未找到</p>
        <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer">返回首页</Link>
      </div>
    );
  }

  const currentChapterData = chapters.find(ch => ch.id === currentChapter);

  return (
    <>
      <Breadcrumb items={[
        { label: '漫画分类', href: '/category/all' },
        { label: comic.category === 'shounen' ? '少年热血' : comic.category === 'shoujo' ? '少女恋爱' : comic.category === 'comedy' ? '搞笑日常' : comic.category === 'fantasy' ? '奇幻冒险' : comic.category === 'horror' ? '悬疑恐怖' : comic.category === 'scifi' ? '科幻未来' : comic.category === 'slice' ? '治愈日常' : comic.category === 'action' ? '动作格斗' : '异世界', href: `/category/${comic.category}` },
        { label: comic.title },
      ]} />

      {readingMode && currentChapterData ? (
        <ReaderView
          comic={comic}
          chapter={currentChapterData}
          chapters={chapters}
          onChapterChange={setCurrentChapter}
          onBack={() => setReadingMode(false)}
        />
      ) : (
        <DetailView
          comic={comic}
          chapters={displayedChapters}
          chapterFilter={chapterFilter}
          onChapterFilterChange={setChapterFilter}
          onStartReading={(chapterId: number) => {
            setCurrentChapter(chapterId);
            setReadingMode(true);
          }}
        />
      )}

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Book",
            "name": comic.title,
            "author": { "@type": "Person", "name": comic.author },
            "description": comic.description,
            "inLanguage": "zh-CN",
            "url": `https://mimei-comic.com/comic/${comic.id}`,
          }),
        }}
      />
    </>
  );
}

function DetailView({
  comic, chapters, chapterFilter, onChapterFilterChange, onStartReading,
}: {
  comic: NonNullable<ReturnType<typeof getComicById>>;
  chapters: ReturnType<typeof getChaptersByComicId>;
  chapterFilter: string;
  onChapterFilterChange: (f: 'all' | 'new') => void;
  onStartReading: (id: number) => void;
}) {
  return (
    <section className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <div className="flex flex-col md:flex-row gap-6 md:gap-8">
        <div className="md:w-72 flex-shrink-0">
          <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200 sticky top-20">
            <img src={comic.cover} alt={comic.title} className="w-full h-full object-cover object-top" />
          </div>
        </div>

        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
            {comic.title}
          </h1>
          <p className="text-sm text-foreground-500 mt-1">作者：{comic.author}</p>

          <div className="flex items-center gap-3 mt-4 flex-wrap">
            <span className="flex items-center gap-1 text-sm text-accent-500 font-semibold whitespace-nowrap">
              <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i> {comic.rating}
            </span>
            <span className="text-sm text-foreground-500">{comic.views} 阅读</span>
            <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
              comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
            }`}>
              {comic.status}
            </span>
            <span className="text-xs text-foreground-400">更新于 {comic.updateTime}</span>
          </div>

          <div className="flex items-center gap-2 mt-4 flex-wrap">
            {comic.tags.map(tag => (
              <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 rounded-full text-xs whitespace-nowrap">{tag}</span>
            ))}
          </div>

          <div className="mt-5 p-4 bg-background-100 rounded-xl">
            <h3 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{comic.description}</p>
          </div>

          <div className="flex items-center gap-3 mt-5">
            <button
              onClick={() => {
                const firstChapter = chapters[chapters.length - 1];
                if (firstChapter) onStartReading(firstChapter.id);
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-white rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-line w-5 h-5 flex items-center justify-center"></i>
              开始阅读
            </button>
            <button
              onClick={() => {
                if (chapters.length > 0) onStartReading(chapters[0].id);
              }}
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-background-200 text-foreground-700 rounded-full font-semibold text-sm hover:bg-background-300 transition-colors whitespace-nowrap cursor-pointer"
            >
              阅读最新章节
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>章节列表</h2>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => onChapterFilterChange('new')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                chapterFilter === 'new' ? 'bg-white text-foreground-900 shadow-sm' : 'text-foreground-500'
              }`}
            >
              最新
            </button>
            <button
              onClick={() => onChapterFilterChange('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                chapterFilter === 'all' ? 'bg-white text-foreground-900 shadow-sm' : 'text-foreground-500'
              }`}
            >
              全部
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {chapters.map(ch => (
            <button
              key={ch.id}
              onClick={() => onStartReading(ch.id)}
              className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-background-100 hover:bg-primary-50 hover:text-primary-600 transition-colors text-sm cursor-pointer"
            >
              <span className="truncate">{ch.title}</span>
              {ch.updateTime === '2026-08-04' && (
                <span className="ml-2 px-1.5 py-0.5 bg-accent-100 text-accent-600 rounded text-[10px] font-medium whitespace-nowrap flex-shrink-0">NEW</span>
              )}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function ReaderView({
  comic, chapter, chapters, onChapterChange, onBack,
}: {
  comic: NonNullable<ReturnType<typeof getComicById>>;
  chapter: ReturnType<typeof getChaptersByComicId>[0];
  chapters: ReturnType<typeof getChaptersByComicId>;
  onChapterChange: (id: number) => void;
  onBack: () => void;
}) {
  const currentIdx = chapters.findIndex(ch => ch.id === chapter.id);
  const prevChapter = chapters[currentIdx + 1] ?? null;
  const nextChapter = chapters[currentIdx - 1] ?? null;

  return (
    <section className="max-w-2xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回详情
        </button>
        <div className="text-sm text-foreground-500">
          {comic.title} · {chapter.title}
        </div>
        <div className="w-16"></div>
      </div>

      <div className="bg-white rounded-xl border border-background-200 p-6 md:p-10 min-h-[60vh]">
        <div className="space-y-6">
          {READER_PLACEHOLDER_IMAGES.map((imgUrl, i) => (
            <div key={i} className="rounded-lg overflow-hidden bg-background-100">
              <img
                src={imgUrl}
                alt={`${comic.title} ${chapter.title} 第${i + 1}页`}
                className="w-full object-contain"
                loading="lazy"
              />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        {prevChapter ? (
          <button
            onClick={() => onChapterChange(prevChapter.id)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-background-100 rounded-full text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
            上一话
          </button>
        ) : (
          <div></div>
        )}
        <select
          value={chapter.id}
          onChange={e => onChapterChange(parseInt(e.target.value))}
          className="px-3 py-2 bg-background-100 rounded-lg text-sm text-foreground-700 cursor-pointer border-none outline-none"
        >
          {chapters.map(ch => (
            <option key={ch.id} value={ch.id}>{ch.title}</option>
          ))}
        </select>
        {nextChapter ? (
          <button
            onClick={() => onChapterChange(nextChapter.id)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            下一话
            <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        ) : (
          <span className="text-xs text-foreground-400">已是最新</span>
        )}
      </div>
    </section>
  );
}